import { doc, getDoc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../../firebase";
import { useAuth } from "../../components/authContext";
import StyledButton from "../components/button";
import ReceiptPreview from "./ReceiptPreview";

interface LineItem {
  label: string;
  price: number;
}

const EditReceipt: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [store, setStore] = useState("Receipt");
  const [items, setItems] = useState<LineItem[]>([]);
  const [tax, setTax] = useState<number>(0);
  const [rawTotal, setRawTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const computedTotal = items.reduce((sum, item) => sum + item.price, 0) + tax;

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!user || !id) return;

      const ref = doc(db, `users/${user.uid}/purchases`, id);
      const snapshot = await getDoc(ref);
      if (!snapshot.exists()) return;

      const data = snapshot.data();
      setStore(data.store || "Receipt");

      const rawText = data.rawText || "";
      const parsedItems = parseRawText(rawText);
      setItems(parsedItems);

      // Try to extract tax and total from OCR text
      const taxMatch = rawText.match(/tax[\s\S]*?\$?([0-9]+\.[0-9]{2})/i);
      const totalMatch = rawText.match(/total[\s\S]*?\$?([0-9]+\.[0-9]{2})/i);

      if (taxMatch) setTax(parseFloat(taxMatch[1]));
      if (totalMatch) setRawTotal(parseFloat(totalMatch[1]));

      setLoading(false);
    };

    fetchReceipt();
  }, [user, id]);

  const parseRawText = (text: string): LineItem[] => {
    const lines = text.split("\n").map((line) => line.trim());
    const result: LineItem[] = [];

    for (let i = 1; i < lines.length; i++) {
      const priceMatch = lines[i].match(/^\$?([0-9]+(?:\.[0-9]{2})?)$/);
      if (priceMatch && lines[i - 1]) {
        result.push({
          label: lines[i - 1],
          price: parseFloat(priceMatch[1]),
        });
      }
    }

    return result;
  };

  const serializeToRawText = (items: LineItem[]): string => {
    return items
      .map((item) => `${item.label}\n$${item.price.toFixed(2)}`)
      .join("\n");
  };

  const handleChange = (
    index: number,
    field: keyof LineItem,
    value: string
  ) => {
    const updated = [...items];
    if (field === "price") {
      const parsed = parseFloat(value);
      updated[index].price = isNaN(parsed) ? 0 : parsed;
    } else {
      updated[index].label = value;
    }
    setItems(updated);
  };

  const handleDelete = (index: number) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
  };

  const handleAdd = () => {
    setItems([...items, { label: "", price: 0 }]);
  };

  const saveReceipt = async () => {
    if (!user || !id) return;
    const ref = doc(db, `users/${user.uid}/purchases`, id);
    await updateDoc(ref, {
      store,
      rawText: serializeToRawText(items),
      tax,
      total: computedTotal,
      items: items.map((item) => ({
        label: item.label,
        price: item.price,
      })),
    });
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div style={{ padding: 20, maxWidth: 600, margin: "0 auto" }}>
      <h2>Edit Receipt</h2>

      <input
        value={store}
        onChange={(e) => setStore(e.target.value)}
        placeholder="Store Name"
        style={{
          width: "100%",
          fontSize: "20px",
          padding: "10px",
          fontWeight: "bold",
          marginBottom: "20px",
        }}
      />

      {items.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "10px",
          }}
        >
          <input
            value={item.label}
            onChange={(e) => handleChange(i, "label", e.target.value)}
            placeholder="Item name"
            style={{ flexGrow: 1, fontSize: "16px", padding: "8px" }}
          />
          <input
            type="number"
            value={item.price}
            onChange={(e) => handleChange(i, "price", e.target.value)}
            placeholder="Price"
            step="0.01"
            style={{ width: "80px", fontSize: "14px", padding: "6px" }}
          />
          <StyledButton
            onClick={() => handleDelete(i)}
            style={{
              padding: "4px 8px",
              fontSize: "16px",
              background: "none",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            🗑️
          </StyledButton>
        </div>
      ))}

      <StyledButton onClick={handleAdd} style={{ marginBottom: 20 }}>
        + Add Item
      </StyledButton>

      <div style={{ marginBottom: "20px" }}>
        <p>
          <strong>Tax:</strong>{" "}
          <input
            type="number"
            value={tax}
            onChange={(e) => {
              const parsed = parseFloat(e.target.value);
              setTax(isNaN(parsed) ? 0 : parsed);
            }}
            step="0.01"
            style={{ width: "100px", padding: "6px", fontSize: "14px" }}
          />
        </p>
        <p>
          <strong>Total from receipt (OCR):</strong> ${rawTotal.toFixed(2)}
        </p>
        <p>
          <strong>Computed total:</strong> ${computedTotal.toFixed(2)}
        </p>
      </div>

      <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
        <StyledButton onClick={saveReceipt}>💾 Save</StyledButton>
        <StyledButton onClick={() => navigate("/foodbudget")}>
          ❌ Cancel
        </StyledButton>
        <StyledButton
          onClick={async () => {
            await saveReceipt(); // ensure saved before redirect
            navigate("/foodbudget");
          }}
        >
          ➡️ Next
        </StyledButton>
      </div>

      <hr style={{ margin: "40px 0" }} />

      <h3>Live Preview</h3>
      <ReceiptPreview
        key={`${tax}-${items.length}-${computedTotal}`} // 👈 triggers re-render
        store={store}
        items={items}
        tax={tax}
        total={computedTotal}
      />
    </div>
  );
};

export default EditReceipt;
